import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ObjPopupInfoVal } from '../../../../../../../classes/obj-popup-info-val';
import { ObjPopupResponseCallApi } from '../../../../../../../classes/obj-popup-response-call-api';
import { PopupDialogAfterClose } from '../../../../../../../interfaces/popup-dialog-after-close';
import { PopupDialogService } from '../../../../../../../services/popup-dialog.service';
import { GoogleCalendarInfo, GoogleEvent, GoogleService } from '../../../../../../../services/google.service';
import { dateRangeValidator } from '../validators/date-validator';
import { timeRangeValidator } from '../validators/time-validator';

export interface GoogleEventDialogData {
  mode: 'create' | 'edit' | 'view';
  calendars: GoogleCalendarInfo[];
  event?: GoogleEvent;
}

@Component({
  selector: 'app-dialog-google-event',
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, TranslateModule],
  templateUrl: './dialog-google-event.component.html',
})
export class DialogGoogleEventComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly google = inject(GoogleService);
  private readonly translate = inject(TranslateService);
  private readonly popupDialogService = inject(PopupDialogService);

  form: FormGroup;
  saving = false;
  mode: GoogleEventDialogData['mode'];
  writableCalendars: GoogleCalendarInfo[] = [];

  constructor(
    public dialogRef: MatDialogRef<DialogGoogleEventComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GoogleEventDialogData,
  ) {
    this.mode = data.mode;
    this.writableCalendars = data.calendars.filter((calendar) => calendar.writable === 1);
    const event = data.event;
    const defaultCalendar = this.writableCalendars.find((calendar) => calendar.primary === 1) ?? this.writableCalendars[0];
    this.form = this.fb.group({
      calendar_id: [event?.calendar_id ?? defaultCalendar?.id ?? '', Validators.required],
      title: [event?.title ?? '', [Validators.required, Validators.maxLength(500)]],
      description: [event?.description ?? ''],
      location: [event?.location ?? ''],
      isallday: [event?.is_all_day === 1],
      date_start: [event?.date_start ?? '', Validators.required],
      date_end: [event?.date_end ?? '', Validators.required],
      time_start: [event?.time_start ?? ''],
      time_end: [event?.time_end ?? ''],
    }, { validators: [dateRangeValidator(), timeRangeValidator()] });

    if (this.mode === 'view') {
      this.form.disable();
    }
  }

  ngOnInit(): void {
    if (this.mode === 'create' && this.writableCalendars.length === 0) {
      this.form.disable();
    }
  }

  save(): void {
    if (this.mode === 'view' || this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    if (value.isallday !== true && (!value.time_start || !value.time_end)) {
      this.form.get('time_start')?.setErrors({ required: true });
      this.form.get('time_end')?.setErrors({ required: true });
      return;
    }
    this.saving = true;
    this.google.saveEvent({
      calendar_id: value.calendar_id,
      event_id: this.data.event?.id ?? null,
      title: value.title,
      description: value.description || null,
      location: value.location || null,
      is_all_day: value.isallday === true,
      date_start: value.date_start,
      date_end: value.date_end,
      time_start: value.isallday ? null : value.time_start,
      time_end: value.isallday ? null : value.time_end,
    }).subscribe({
      next: () => this.dialogRef.close({ action: 'refresh' }),
      error: () => {
        this.saving = false;
      },
    });
  }

  remove(): void {
    if (!this.data.event || this.saving) {
      return;
    }
    const popup = new ObjPopupResponseCallApi();
    popup.titolo = this.translate.instant('CALENDAR.GOOGLE.DELETE_TITLE');
    popup.messaggio = this.translate.instant('CALENDAR.GOOGLE.DELETE_CONFIRM');
    popup.dialogResultGo = true;
    const info = new ObjPopupInfoVal();
    info.pulsanteGo = true;
    info.testoPulsanteGo = this.translate.instant('CALENDAR.GOOGLE.DELETE');
    popup.valore = info;
    this.popupDialogService.popUpDialogConfirm(popup, 'warning').afterClosed().subscribe((result: PopupDialogAfterClose) => {
      if (!result?.avanti || !this.data.event) {
        return;
      }
      this.saving = true;
      this.google.deleteEvent(this.data.event.calendar_id, this.data.event.id).subscribe({
        next: () => this.dialogRef.close({ action: 'refresh' }),
        error: () => {
          this.saving = false;
        },
      });
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
